import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { Loading, ErrorNote } from '../components/ui.jsx';

/**
 * Accepting an invitation to somebody else's household.
 *
 * Inside the sign-in gate deliberately. Joining needs an account, and the gate
 * already returns to the page it started from once a session lands -- so
 * somebody who follows the link signed out arrives back here, token intact,
 * rather than at a home page with nothing to explain what happened.
 *
 * The preview is a separate step from accepting, and both are separate from
 * arriving. A link that joined on page load would let a URL in a message move
 * somebody between households without them agreeing to anything.
 */
export default function Join() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!token) { setError(new Error('This link is missing its invitation code.')); setLoading(false); return undefined; }
    api.get(`/household/invites/preview?token=${encodeURIComponent(token)}`)
      .then((value) => { if (alive) setInvite(value); })
      .catch((value) => { if (alive) setError(value); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const accept = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.post('/household/invites/accept', { token });
      // A full load rather than a route change: everything cached in this tab
      // belongs to the household they just left.
      window.location.assign('/');
    } catch (value) {
      setError(value);
      setBusy(false);
    }
  };

  if (loading) return <Loading rows={3} />;

  return (
    <div className="stack-lg">
      <section>
        <div className="section-title"><h2>Join a household</h2></div>
        <div className="card stack">
          <ErrorNote error={error} />

          {invite && (
            <>
              <p>
                You have been invited to <strong>{invite.household_name}</strong>.
              </p>
              <p className="small muted">
                Joining moves you into that household. Anything in your own pantry stays where it
                is, and you will no longer see it &mdash; so if you have food, receipts or recipes
                saved, take a copy from Settings first.
              </p>
              <button type="button" className="primary full" disabled={busy} onClick={accept}>
                Join {invite.household_name}
              </button>
            </>
          )}

          <button type="button" className="full" disabled={busy} onClick={() => navigate('/')}>
            {invite ? 'Not now' : 'Back to the app'}
          </button>
        </div>
      </section>
    </div>
  );
}
